import React, { useState, useMemo, useEffect } from 'react';
import styled, { withTheme } from 'styled-components/macro';
import {
  Icon, Text, Spacer, Button
} from 'components/atoms';
import { useDropzone } from 'react-dropzone';
import { themeType, imageType } from 'types';
import PropTypes from 'prop-types';
// import { generatePortalImg } from 'services/generators';

const MediaInput = ({
  theme,
  onChange,
  initialImage,
  initialFile,
  onEdit,
  name,
  isEditable,
  isFormActive,
}) => {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(false);
  const [isHover, setHover] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [hasRemoved, setHasRemoved] = useState(false);

  const updatePreview = file => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = event => {
      setPreview(event.target.result);
    };
    setError(null);
    setCurrentFile(file);
  };

  useEffect(() => {
    if (hasRemoved) {
      setPreview(null);
      return;
    }
    if (currentFile) updatePreview(currentFile);
    if (initialFile) updatePreview(initialFile);
    if (!currentFile && !initialFile && !initialImage) {
      setPreview(null);
      return;
    }
    // console.log('initialImage >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>', initialImage);

    if (initialImage && initialImage.src && !currentFile) { setPreview(initialImage.src); }
    if (initialImage && initialImage.url && !currentFile) { setPreview(initialImage.url); }
    // TODO ASK ANTONIO ABOUT THIS

    // if (initialImage && initialImage.url && !currentFile) {
    //   setPreview(
    //     generatePortalImg({
    //       src: initialImage.url,
    //       isLocal:
    //         initialImage.isLocal === undefined ? true : initialImage.isLocal,
    //       height: 500,
    //     })
    //   );
    // }
  }, [initialImage, currentFile, initialFile, hasRemoved]);

  const hasImage = useMemo(() => !!preview, [preview]);

  const handleUpload = file => {
    onChange(file);
    if (onEdit) onEdit();
  };

  const handleChange = file => {
    if (!file) return;
    if (!file && !currentFile) {
      setPreview(null);
      onChange(null);
      return;
    }
    if (!/\.(jpe?g|png|svg)$/i.test(file.name)) {
      setPreview(null);
      setError(
        'Formato não suportado, apenas são permitidos ficheiros jpg ou png.'
      );
      return;
    }
    updatePreview(file);
    handleUpload(file);
  };

  const onDrop = files => {
    handleChange(files[0]);
  };

  // when the form is not visible, reset the hasRemoved
  useEffect(() => {
    setHasRemoved(false);
  }, [isFormActive]);

  const onRemove = e => {
    e.stopPropagation();
    setHasRemoved(true);
    setPreview(null);
    handleChange(null);
    setCurrentFile(null);
  };

  const color = useMemo(() => {
    if (isHover) return theme.colors.primary;
    if (error) return theme.colors.danger;
    return theme.colors.base;
  }, [
    isHover,
    theme.colors.primary,
    theme.colors.base,
    error,
    theme.colors.danger,
  ]);

  const {
    getRootProps, getInputProps, isDragActive, inputRef
  } = useDropzone({
    onDrop,
  });

  const onEditImage = e => {
    e.stopPropagation();
    inputRef.current.click();
  };

  const computedText = useMemo(() => {
    if (isHover) return 'Upload';
    if (error) return 'Formato inválido, tem de ser .jpg ou .png';
    return isDragActive ? 'Drop here' : 'Upload';
  }, [isDragActive, error, isHover]);

  const initialImageExtension = image => (image && image.url ? image.url.slice(image.url.lastIndexOf('.')) : 'image/*')

  return (
    <>
      <Preview.Wrapper
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        hasImage={hasImage}
      >
        {isHover && (
          <>
            <Preview.HoverCenter>
              <Icon icon="edit" color="#FFF" />
              <Text modifiers={['inverse']}>Alterar imagem</Text>
            </Preview.HoverCenter>
          </>
        )}
        <Preview img={preview} />
      </Preview.Wrapper>
      <DropzoneWrapper hasImage={hasImage}>
        <Dropzone
          {...getRootProps()}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          title="Carregar imagem"
          error={error}
          preview={preview}
        >
          <input
            name={name}
            {...getInputProps()}
            accept={initialImageExtension(initialImage)}
            onChange={e => handleChange(e.target.files[0])}
          />
          <Spacer bottom="xs3">
            <Icon icon="add_photo" color={color} />
          </Spacer>
          <Text>{computedText}</Text>
        </Dropzone>
      </DropzoneWrapper>
    </>
  );
};

MediaInput.propTypes = {
  theme: themeType.isRequired,
  onChange: PropTypes.func,
  onEdit: PropTypes.func,
  initialImage: imageType,
  initialFile: PropTypes.shape({}),
  isEditable: PropTypes.bool,
  name: PropTypes.string,
  isFormActive: PropTypes.bool,
};

MediaInput.defaultProps = {
  onChange: () => null,
  onEdit: null,
  initialImage: null,
  isEditable: false,
  name: '',
  initialFile: null,
  isFormActive: false,
};

const DropzoneWrapper = styled.div`
  ${props => props.hasImage && 'display: none'};
  width: 100%;
  height: 100%;
`;

const Preview = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-image: url(${props => props.img});
  background-size: 100% auto;
  background-repeat: no-repeat;
  background-position: center;
`;

Preview.Wrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  border: 1px solid ${props => props.theme.colors.bgDark};
  ${props => !props.hasImage && 'display: none'};
`;

Preview.Hover = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  padding: ${props => props.theme.spacing.xs2};
  z-index: ${props => props.theme.values.zIndex.l};
  top: 0;
  left: 0;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  cursor: pointer;
`;

Preview.HoverCenter = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  z-index: ${props => props.theme.values.zIndex.l};
  justify-content: center;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${props => props.theme.spacing.xs};
  background-color: ${props => props.theme.colors.bgHover};
`;

const Dropzone = styled.div`
  width: 100%;
  height: 100%;
  border: 1px
    ${props => (props.preview
    ? `dashed ${props.theme.colors.greyLighter}`
    : `dashed ${props.theme.colors.base}`)};
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  cursor: pointer;
  text-align: center;
  padding: ${props => props.theme.spacing.xs2};
  position: relative;
  ${Text} {
    color: ${props => (props.error ? props.theme.colors.danger : props.theme.colors.base)};
  }
  &:hover {
    border: 1px
      ${props => (props.preview
    ? `dashed ${props.theme.colors.greyLighter}`
    : `dashed ${props.theme.colors.primary}`)};
    ${Text} {
      color: ${props => props.theme.colors.primary};
    }
  }
  &:focus {
    outline: 0;
  }
`;

export default withTheme(MediaInput);
